import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';

/** A paid campaign with an external link, held until an admin approves it. */
export interface PromotionInReview {
  promid: number;
  name: string | null;
  description: string | null;
  link_ext: string | null;
  uid: number | null;
  oid: number | null;
  eid: number | null;
  date_added: string | null;
}

type ReviewList = { data: PromotionInReview[]; total: number };

const BASE = '/api/admin/promotions';
const KEY = ['promotion-review'];

// ponytail: one page of 100, no pager. Add paging when the queue outgrows it.
export const usePromotionReviewQuery = () => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: KEY,
    queryFn: withErrorHandling(async () => {
      const res = await request<{ data?: ReviewList } & Partial<ReviewList>>(`${BASE}/review?page_size=100`);
      const body = (res?.data && !Array.isArray(res.data) ? res.data : res) as ReviewList;
      return { data: body.data ?? [], total: body.total ?? 0 };
    }, 'Failed to load the review queue'),
  });
};

export const useDecidePromotionReviewMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: withErrorHandling(
      async ({ promid, approve, reason }: { promid: number; approve: boolean; reason?: string }) =>
        request(`${BASE}/${promid}/review/${approve ? 'approve' : 'reject'}`, 'POST', reason ? { reason } : {}),
      'Failed to record the decision',
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
};
